import { z } from 'zod';
import { centsSchema, cuidSchema, plainDateSchema } from './common.js';
/**
 * Contratos da Fase 2. Os mesmos objetos validam a entrada na API, filtram a
 * saida e alimentam o OpenAPI — e a web reusa para validar antes de enviar.
 */
export const accountTypeSchema = z.enum([
    'CHECKING',
    'SAVINGS',
    'PAYMENT',
    'CASH',
    'INVESTMENT',
    'CREDIT_CARD',
]);
export const transactionKindSchema = z.enum(['INCOME', 'EXPENSE', 'TRANSFER']);
export const transactionStatusSchema = z.enum(['PENDING', 'CLEARED']);
export const transactionSourceSchema = z.enum([
    'MANUAL',
    'OPEN_FINANCE',
    'OFX',
    'CSV',
    'NFCE',
    'BOLETO',
    /**
     * Baixa de conta a pagar. Existe separado de BOLETO porque nem toda conta
     * fixa tem boleto (aluguel no PIX, mensalidade em debito) e porque e essa
     * origem, junto com o identificador `payable:<id>`, que impede pagar a
     * mesma conta duas vezes: o indice unico barra na segunda tentativa.
     */
    'PAYABLE',
]);
export const invoiceStatusSchema = z.enum(['OPEN', 'CLOSED', 'PAID']);
/**
 * O lado de uma categoria.
 *
 * TRANSFER nao e um terceiro "lado do dinheiro": e o rotulo de um movimento
 * que nao e nem entrada nem saida. Ele existe para a lista poder dizer
 * "fatura do cartao" em vez de apenas "transferencia", e fica fora de toda
 * soma — os totais e o grafico filtram por INCOME e EXPENSE.
 */
export const categoryKindSchema = z.enum(['INCOME', 'EXPENSE', 'TRANSFER']);
/** De onde veio um dado que o sistema preencheu sozinho. */
export const dataSourceSchema = z.enum([
    'BANK',
    'RULE',
    'AI',
    'USER',
    'LEARNED',
    'DERIVED',
    'DEFAULT',
]);
/** Cores nomeadas, resolvidas pelos tokens de design em cada cliente. */
export const colorTokenSchema = z.enum([
    'ink',
    'amber',
    'green',
    'red',
    'blue',
    'violet',
    'teal',
    'rose',
]);
const shortText = z.string().trim().min(1).max(120);
const longText = z.string().trim().max(2000);
// ------------------------------------------------------------- instituicoes
export const createInstitutionSchema = z.object({
    name: shortText,
    shortName: z.string().trim().max(40).optional(),
    code: z.string().trim().max(20).optional(),
    colorToken: colorTokenSchema.default('ink'),
});
export const institutionSchema = z.object({
    id: z.string(),
    name: z.string(),
    shortName: z.string().nullable(),
    code: z.string().nullable(),
    colorToken: z.string(),
    accountCount: z.number().int(),
});
// -------------------------------------------------------------------- contas
const creditCardFields = {
    creditLimitCents: centsSchema.optional(),
    closingDay: z.coerce.number().int().min(1).max(31).optional(),
    dueDay: z.coerce.number().int().min(1).max(31).optional(),
    paymentAccountId: cuidSchema.optional(),
};
export const createAccountSchema = z
    .object({
    name: shortText,
    type: accountTypeSchema,
    institutionId: cuidSchema.optional(),
    colorToken: colorTokenSchema.default('ink'),
    icon: z.string().trim().max(40).optional(),
    initialBalanceCents: centsSchema.default('0'),
    initialBalanceDate: plainDateSchema.optional(),
    includeInNetWorth: z.boolean().default(true),
    ...creditCardFields,
})
    .superRefine((value, ctx) => {
    const isCard = value.type === 'CREDIT_CARD';
    if (isCard) {
        // Sem os dias do ciclo nao ha como saber em que fatura uma compra cai.
        if (value.closingDay === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['closingDay'],
                message: 'Informe o dia de fechamento do cartao.',
            });
        }
        if (value.dueDay === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dueDay'],
                message: 'Informe o dia de vencimento do cartao.',
            });
        }
        if (value.closingDay !== undefined && value.dueDay === value.closingDay) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dueDay'],
                message: 'O vencimento nao pode cair no mesmo dia do fechamento.',
            });
        }
    }
    else {
        for (const field of [
            'creditLimitCents',
            'closingDay',
            'dueDay',
            'paymentAccountId',
        ]) {
            if (value[field] !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [field],
                    message: 'Este campo so vale para cartao de credito.',
                });
            }
        }
    }
});
export const updateAccountSchema = z.object({
    name: shortText.optional(),
    institutionId: cuidSchema.nullable().optional(),
    colorToken: colorTokenSchema.optional(),
    icon: z.string().trim().max(40).nullable().optional(),
    includeInNetWorth: z.boolean().optional(),
    creditLimitCents: centsSchema.nullable().optional(),
    closingDay: z.coerce.number().int().min(1).max(31).nullable().optional(),
    dueDay: z.coerce.number().int().min(1).max(31).nullable().optional(),
    paymentAccountId: cuidSchema.nullable().optional(),
    archived: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const accountSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: accountTypeSchema,
    institutionId: z.string().nullable(),
    institutionName: z.string().nullable(),
    colorToken: z.string(),
    icon: z.string().nullable(),
    /** Centavos como texto decimal: bigint nao sobrevive ao JSON. */
    initialBalanceCents: z.string(),
    initialBalanceDate: z.string(),
    currentBalanceCents: z.string(),
    includeInNetWorth: z.boolean(),
    creditLimitCents: z.string().nullable(),
    availableCreditCents: z.string().nullable(),
    closingDay: z.number().int().nullable(),
    dueDay: z.number().int().nullable(),
    paymentAccountId: z.string().nullable(),
    archived: z.boolean(),
    sortOrder: z.number().int(),
    transactionCount: z.number().int(),
});
export const accountListSchema = z.object({
    accounts: z.array(accountSchema),
    totals: z.object({
        /** Soma das contas que entram no patrimonio, cartoes inclusos. */
        netWorthCents: z.string(),
        /** So o que esta disponivel para gastar hoje (exclui cartao e investimento). */
        availableCents: z.string(),
        creditCardDebtCents: z.string(),
    }),
});
// --------------------------------------------------------------- categorias
export const createCategorySchema = z.object({
    name: shortText,
    kind: categoryKindSchema,
    parentId: cuidSchema.optional(),
    colorToken: colorTokenSchema.default('ink'),
    icon: z.string().trim().max(40).optional(),
});
export const updateCategorySchema = z.object({
    name: shortText.optional(),
    colorToken: colorTokenSchema.optional(),
    icon: z.string().trim().max(40).nullable().optional(),
    parentId: cuidSchema.nullable().optional(),
    archived: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const categoryNodeSchema = z.lazy(() => z.object({
    id: z.string(),
    name: z.string(),
    kind: categoryKindSchema,
    parentId: z.string().nullable(),
    colorToken: z.string(),
    icon: z.string().nullable(),
    isSystem: z.boolean(),
    archived: z.boolean(),
    sortOrder: z.number().int(),
    children: z.array(categoryNodeSchema),
}));
export const categoryTreeSchema = z.object({ categories: z.array(categoryNodeSchema) });
// -------------------------------------------------------------------- tags
export const createTagSchema = z.object({
    name: z.string().trim().min(1).max(40),
    colorToken: colorTokenSchema.default('ink'),
});
export const tagSchema = z.object({
    id: z.string(),
    name: z.string(),
    colorToken: z.string(),
    transactionCount: z.number().int(),
});
// -------------------------------------------------------------- transacoes
export const splitInputSchema = z.object({
    categoryId: cuidSchema,
    amountCents: centsSchema,
    notes: z.string().trim().max(200).optional(),
});
/**
 * O usuario informa o valor SEM sinal e diz se e entrada ou saida; o servidor
 * aplica o sinal. Deixar o cliente mandar o sinal convidaria a uma despesa
 * positiva entrar no banco e inflar o saldo.
 */
export const createTransactionSchema = z
    .object({
    accountId: cuidSchema,
    date: plainDateSchema,
    kind: z.enum(['INCOME', 'EXPENSE']),
    amountCents: centsSchema.refine((value) => value > 0n, 'Informe um valor maior que zero.'),
    description: shortText,
    categoryId: cuidSchema.optional(),
    notes: longText.optional(),
    status: transactionStatusSchema.default('CLEARED'),
    tagIds: z.array(cuidSchema).max(20).default([]),
    splits: z.array(splitInputSchema).max(20).optional(),
    /** Parcelas. Vale apenas para compra no cartao. */
    installments: z.coerce.number().int().min(1).max(72).default(1),
})
    .superRefine((value, ctx) => {
    if (value.splits && value.splits.length > 0 && value.categoryId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['categoryId'],
            message: 'Um lancamento dividido nao tem categoria unica.',
        });
    }
    if (value.installments > 1 && value.splits && value.splits.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['splits'],
            message: 'Compra parcelada com divisao por categoria ainda nao e suportada.',
        });
    }
});
export const updateTransactionSchema = z.object({
    date: plainDateSchema.optional(),
    amountCents: centsSchema
        .refine((value) => value > 0n, 'Informe um valor maior que zero.')
        .optional(),
    description: shortText.optional(),
    categoryId: cuidSchema.nullable().optional(),
    notes: longText.nullable().optional(),
    status: transactionStatusSchema.optional(),
    tagIds: z.array(cuidSchema).max(20).optional(),
    splits: z.array(splitInputSchema).max(20).nullable().optional(),
    /** Quem recebeu, escrito a mao. Trava: nem varredura nem IA mexem depois. */
    counterpartyName: z.string().trim().max(160).nullable().optional(),
    /**
     * Corrige a direcao de um lancamento.
     *
     * TRANSFER fica de fora: uma transferencia tem duas pernas e virar uma
     * sozinha deixaria o saldo de uma conta certo e o da outra errado. Para
     * desfazer um par existe "Nao e transferencia".
     */
    kind: z.enum(['INCOME', 'EXPENSE']).optional(),
});
export const createTransferSchema = z
    .object({
    fromAccountId: cuidSchema,
    toAccountId: cuidSchema,
    date: plainDateSchema,
    amountCents: centsSchema.refine((value) => value > 0n, 'Informe um valor maior que zero.'),
    description: shortText.default('Transferencia'),
    notes: longText.optional(),
})
    .superRefine((value, ctx) => {
    if (value.fromAccountId === value.toAccountId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['toAccountId'],
            message: 'A conta de destino precisa ser diferente da de origem.',
        });
    }
});
export const payInvoiceSchema = z.object({
    fromAccountId: cuidSchema,
    date: plainDateSchema,
    /** Omitido paga o total da fatura. */
    amountCents: centsSchema
        .refine((value) => value > 0n, 'Informe um valor maior que zero.')
        .optional(),
});
export const transactionFiltersSchema = z.object({
    accountId: cuidSchema.optional(),
    categoryId: cuidSchema.optional(),
    tagId: cuidSchema.optional(),
    kind: transactionKindSchema.optional(),
    status: transactionStatusSchema.optional(),
    source: transactionSourceSchema.optional(),
    cardInvoiceId: cuidSchema.optional(),
    from: plainDateSchema.optional(),
    to: plainDateSchema.optional(),
    minCents: centsSchema.optional(),
    maxCents: centsSchema.optional(),
    search: z.string().trim().max(120).optional(),
    /** Transferencias poluem a lista de gastos; por padrao ficam de fora. */
    includeTransfers: z.coerce.boolean().default(true),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const transactionSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    date: z.string(),
    amountCents: z.string(),
    kind: transactionKindSchema,
    status: transactionStatusSchema,
    source: transactionSourceSchema,
    description: z.string(),
    /**
     * O nome legivel, derivado da descricao do banco.
     *
     * Anda junto com `description`, e nao no lugar dela: a tela mostra
     * "Assinatura Claude" e guarda "Anthropic* Claude Sub" embaixo, porque
     * conferir com a fatura exige o texto que o banco mandou.
     */
    displayName: z.string(),
    notes: z.string().nullable(),
    categoryId: z.string().nullable(),
    categoryName: z.string().nullable(),
    categoryPath: z.string().nullable(),
    colorToken: z.string().nullable(),
    /** Quem recebeu (saida) ou quem mandou (entrada). Null quando o banco nao diz. */
    counterpartyName: z.string().nullable(),
    /** CPF/CNPJ ja mascarado: nunca sai inteiro da API. */
    counterpartyDocument: z.string().nullable(),
    counterpartyKind: z.enum(['MERCHANT', 'PERSON', 'COMPANY']).nullable(),
    /**
     * De onde saiu cada dado preenchido. E o que separa um fato de uma
     * aproximacao numa tela em que os dois ocupam a mesma coluna.
     */
    counterpartySource: dataSourceSchema.nullable(),
    categorySource: dataSourceSchema.nullable(),
    /** A frase da IA, quando foi ela quem escolheu a categoria. */
    categoryNote: z.string().nullable(),
    /** PIX, TED, BOLETO, CARTAO. */
    paymentMethod: z.string().nullable(),
    transferGroupId: z.string().nullable(),
    transferCounterpartAccountName: z.string().nullable(),
    cardInvoiceId: z.string().nullable(),
    paidInvoiceId: z.string().nullable(),
    purchaseGroupId: z.string().nullable(),
    installmentNumber: z.number().int().nullable(),
    installmentTotal: z.number().int().nullable(),
    tags: z.array(z.object({ id: z.string(), name: z.string(), colorToken: z.string() })),
    splits: z.array(z.object({
        id: z.string(),
        categoryId: z.string(),
        categoryName: z.string(),
        amountCents: z.string(),
        notes: z.string().nullable(),
    })),
    attachmentCount: z.number().int(),
});
/**
 * A resposta de uma edicao, com o que o sistema aprendeu com ela.
 *
 * O campo e opcional e quase sempre vem nulo. Quando vem preenchido, a tela
 * precisa dizer o que aconteceu: uma regra nasceu e outros lancamentos
 * mudaram de categoria junto. Aplicar em silencio seria o pior dos mundos —
 * a pessoa so descobriria ao estranhar o relatorio do mes.
 */
export const aprendizadoSchema = z.object({
    chave: z.string(),
    campo: z.enum(['COUNTERPARTY', 'DESCRIPTION']),
    categoryName: z.string(),
    aplicados: z.number().int(),
    atualizou: z.boolean(),
    ruleId: z.string(),
});
export const transactionUpdatedSchema = transactionSchema.extend({
    aprendizado: aprendizadoSchema.nullable(),
});
/**
 * O que chegou sozinho e ninguem olhou ainda.
 *
 * `total` vem separado da lista porque a pagina tem teto: o numero do
 * distintivo precisa ser o real, e nao "50" quando ha 130 esperando.
 */
export const pendingReviewSchema = z.object({
    total: z.number().int(),
    transactions: z.array(transactionSchema),
    /** Chegou hoje e ja foi conferido: registro do dia, nao pendencia. */
    conferidasHoje: z.array(transactionSchema),
});
export const transactionListSchema = z.object({
    transactions: z.array(transactionSchema),
    nextCursor: z.string().nullable(),
    totals: z.object({
        incomeCents: z.string(),
        expenseCents: z.string(),
        netCents: z.string(),
        count: z.number().int(),
    }),
});
// ------------------------------------------------------------------ faturas
export const invoiceSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    referenceMonth: z.string(),
    closingDate: z.string(),
    dueDate: z.string(),
    status: invoiceStatusSchema,
    paidAt: z.string().nullable(),
    totalCents: z.string(),
    paidCents: z.string(),
    transactionCount: z.number().int(),
});
export const invoiceListSchema = z.object({ invoices: z.array(invoiceSchema) });
// ---------------------------------------------------------------- dashboard
export const dashboardSchema = z.object({
    referenceMonth: z.string(),
    totalBalanceCents: z.string(),
    netWorthCents: z.string(),
    monthIncomeCents: z.string(),
    monthExpenseCents: z.string(),
    monthNetCents: z.string(),
    creditCardDebtCents: z.string(),
    topCategories: z.array(z.object({
        categoryId: z.string().nullable(),
        name: z.string(),
        colorToken: z.string(),
        totalCents: z.string(),
        share: z.number(),
    })),
    openInvoices: z.array(z.object({
        id: z.string(),
        accountName: z.string(),
        dueDate: z.string(),
        totalCents: z.string(),
        status: invoiceStatusSchema,
    })),
    recentTransactions: z.array(transactionSchema),
});
// --------------------------------------------------- sugestao de categoria
/**
 * A IA sugere; a pessoa decide.
 *
 * `available: false` nao e erro: e o estado normal de um servidor sem a
 * assinatura configurada, ou de uma assinatura que bateu o limite. A tela
 * mostra o motivo e segue funcionando sem a sugestao.
 */
export const categorySuggestionsSchema = z.object({
    available: z.boolean(),
    unavailableReason: z.string().nullable(),
    /** Quantos lancamentos sem categoria foram analisados. */
    analyzed: z.number().int(),
    suggestions: z.array(z.object({
        transactionId: z.string(),
        description: z.string(),
        amountCents: z.string(),
        counterpartyName: z.string().nullable(),
        categoryId: z.string(),
        categoryName: z.string(),
        colorToken: z.string(),
        reason: z.string(),
    })),
});
export const applySuggestionsSchema = z.object({
    /** Só o que a pessoa confirmou na tela. */
    accepted: z
        .array(z.object({
        transactionId: cuidSchema,
        categoryId: cuidSchema,
        /** A frase da IA que a tela mostrou, para virar legenda no lançamento. */
        reason: z.string().max(200).nullable().optional(),
    }))
        .min(1)
        .max(200),
});
/**
 * A leitura do mês.
 *
 * Os fatos e o texto viajam separados de propósito: o número é apurado pelo
 * sistema e a frase é escrita pela IA, e a tela precisa poder mostrar o
 * primeiro mesmo quando o segundo não veio.
 */
/**
 * O mesmo dinheiro, banco por banco.
 *
 * A fatura do cartão viaja separada do saldo de propósito: ela ainda não saiu
 * da conta, e somá-la ali faria o dinheiro parecer menor do que é.
 */
export const banksSchema = z.object({
    bancos: z.array(z.object({
        nome: z.string(),
        institutionId: z.string().nullable(),
        saldoCents: z.string(),
        faturaCents: z.string(),
        contas: z.array(z.object({
            id: z.string(),
            name: z.string(),
            type: accountTypeSchema,
            currentBalanceCents: z.string(),
        })),
    })),
});
export const insightsSchema = z.object({
    month: z.string(),
    facts: z.array(z.object({
        id: z.string(),
        tipo: z.enum([
            'RESULTADO',
            'MAIOR_CATEGORIA',
            'SUBIU',
            'ASSINATURAS',
            'MAIOR_GASTO',
            'REPETIDO',
        ]),
        titulo: z.string(),
        valorCents: z.string(),
        sobre: z.string().nullable(),
        contagem: z.number().int().nullable(),
        percentual: z.number().nullable(),
    })),
    reading: z.array(z.object({ factId: z.string(), texto: z.string() })),
    aiAvailable: z.boolean(),
    aiUnavailableReason: z.string().nullable(),
    generatedAt: z.string(),
});
/**
 * O detalhe de um quadro da leitura: os lançamentos por trás do número.
 *
 * Os totais vêm apurados do servidor, e não somados na tela, para que a
 * conferência que a pessoa foi fazer ali seja contra o mesmo cálculo que
 * produziu o quadro.
 */
export const insightsDetailSchema = z.object({
    factId: z.string(),
    titulo: z.string(),
    valorCents: z.string(),
    /** Como o número foi apurado — e, em dois casos, por que a soma difere. */
    explicacao: z.string(),
    transactions: z.array(transactionSchema),
    totais: z.object({
        entradasCents: z.string(),
        saidasCents: z.string(),
        liquidoCents: z.string(),
        quantidade: z.number().int(),
    }),
});
// ------------------------------------------------------------- patrimonio
export const assetKindSchema = z.enum([
    'REAL_ESTATE',
    'VEHICLE',
    'INVESTMENT',
    'RECEIVABLE',
    'OTHER',
]);
export const createAssetSchema = z.object({
    name: shortText,
    kind: assetKindSchema,
    colorToken: colorTokenSchema.default('ink'),
    notes: longText.nullish(),
    valueCents: centsSchema,
    debtCents: centsSchema.optional(),
    /** Data da avaliacao. Ausente significa hoje. */
    valuedOn: plainDateSchema.optional(),
    includeInNetWorth: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
/**
 * Valor e divida ficam de fora de proposito.
 *
 * Mudar quanto um bem vale e uma avaliacao nova, com data, e vai por
 * `/bens/:id/avaliacoes`. Se coubesse aqui, daria para corrigir o numero sem
 * deixar rastro e o historico deixaria de ser historico.
 */
export const updateAssetSchema = z
    .object({
    name: shortText.optional(),
    kind: assetKindSchema.optional(),
    colorToken: colorTokenSchema.optional(),
    notes: longText.nullish(),
    includeInNetWorth: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    archived: z.boolean().optional(),
})
    // Estrito porque o padrao do Zod e descartar o campo desconhecido em
    // silencio. Quem mandasse `valueCents` aqui receberia 200 e nada teria
    // mudado — a pior resposta possivel. Recusar diz onde e o lugar certo.
    .strict();
export const revalueAssetSchema = z.object({
    valueCents: centsSchema,
    debtCents: centsSchema.optional(),
    valuedOn: plainDateSchema.optional(),
    /** De onde saiu o numero: avaliacao, FIPE, IPTU, estimativa. */
    source: z.string().trim().max(120).nullish(),
    notes: longText.nullish(),
});
export const assetSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: assetKindSchema,
    colorToken: z.string(),
    notes: z.string().nullable(),
    valueCents: z.string(),
    debtCents: z.string(),
    netCents: z.string(),
    valuedOn: z.string(),
    includeInNetWorth: z.boolean(),
    archived: z.boolean(),
    sortOrder: z.number().int(),
    valuationCount: z.number().int(),
});
export const assetsSummarySchema = z.object({
    assets: z.array(assetSchema),
    grossCents: z.string(),
    debtCents: z.string(),
    netCents: z.string(),
});
export const assetValuationSchema = z.object({
    id: z.string(),
    valueCents: z.string(),
    debtCents: z.string(),
    netCents: z.string(),
    valuedOn: z.string(),
    source: z.string().nullable(),
    notes: z.string().nullable(),
});
/**
 * O patrimonio inteiro, numa resposta so.
 *
 * E a tela de abertura de qualquer sistema desse tipo: um numero grande e a
 * composicao dele. Separar contas, cartao e bens importa porque sao naturezas
 * diferentes de dinheiro — o da conta se gasta hoje, o do imovel nao.
 */
export const netWorthSchema = z.object({
    totalCents: z.string(),
    /** Contas liquidas: corrente, poupanca, pagamento, dinheiro. */
    accountsCents: z.string(),
    /** Contas de investimento ligadas a bancos. */
    investmentsCents: z.string(),
    /** Divida de cartao em aberto. Positiva, e subtraida do total. */
    creditCardDebtCents: z.string(),
    /** Bens: valor de mercado. */
    assetsCents: z.string(),
    /** Financiamentos dos bens. Positivos, subtraidos do total. */
    assetDebtCents: z.string(),
    composition: z.array(z.object({
        key: z.string(),
        label: z.string(),
        colorToken: z.string(),
        amountCents: z.string(),
        share: z.number(),
    })),
});
// -------------------------------------------------------- para quem eu pago
/**
 * O outro lado do gasto.
 *
 * O painel responde "no que eu gastei" pelas categorias. Isto responde "para
 * quem", que e a pergunta que se faz primeiro ao abrir um extrato.
 */
export const payeesSchema = z.object({
    referenceMonth: z.string(),
    /** Soma de todas as saidas do mes, inclusive as sem recebedor identificado. */
    totalCents: z.string(),
    payees: z.array(z.object({
        name: z.string(),
        kind: z.enum(['MERCHANT', 'PERSON', 'COMPANY']).nullable(),
        document: z.string().nullable(),
        totalCents: z.string(),
        count: z.number().int(),
        lastDate: z.string(),
        categoryName: z.string().nullable(),
        colorToken: z.string().nullable(),
    })),
    /**
     * O que o banco nao soube atribuir a ninguem. Aparece na tela em vez de
     * ser escondido: sem isso, a soma das linhas nao bate com o total do mes e
     * ninguem entende por que.
     */
    unidentified: z.object({ count: z.number().int(), totalCents: z.string() }),
});
// ------------------------------------------------------------------ anexos
export const attachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    createdAt: z.string(),
});
export const attachmentListSchema = z.object({ attachments: z.array(attachmentSchema) });
/**
 * Para onde o saldo vai.
 *
 * Centavos viajam como texto, pela mesma razão do resto do sistema: `bigint`
 * não sobrevive ao JSON, e converter para número aqui seria reintroduzir o
 * ponto flutuante justamente no lugar em que se decide se a conta fecha.
 *
 * `atrasado` e `faturaAberta` viajam à parte porque são as duas incertezas da
 * curva, e uma curva que esconde a própria incerteza é pior que nenhuma: a
 * primeira pode ser conta já paga sem baixa, a segunda ainda vai crescer até
 * o cartão fechar.
 */
export const projecaoSchema = z.object({
    hoje: z.string(),
    ate: z.string(),
    saldoHojeCents: z.string(),
    dias: z.array(z.object({
        date: z.string(),
        saldoCents: z.string(),
        entradaCents: z.string(),
        saidaCents: z.string(),
        eventos: z.array(z.object({
            id: z.string(),
            origem: z.enum(['COMPROMISSO', 'FATURA']),
            descricao: z.string(),
            kind: z.enum(['INCOME', 'EXPENSE']),
            amountCents: z.string(),
            atrasado: z.boolean(),
        })),
    })),
    menorSaldo: z.object({ date: z.string(), saldoCents: z.string() }),
    primeiroNegativo: z.object({ date: z.string(), saldoCents: z.string() }).nullable(),
    totalEntradaCents: z.string(),
    totalSaidaCents: z.string(),
    atrasadoCents: z.string(),
    atrasados: z.array(z.object({
        id: z.string(),
        descricao: z.string(),
        kind: z.enum(['INCOME', 'EXPENSE']),
        amountCents: z.string(),
    })),
    faturaAbertaCents: z.string(),
});
//# sourceMappingURL=finance.js.map